import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { LoroDoc } from "loro-crdt";
import { slotTime, unplanTask, type PlannedSlot } from "../planner/ops";

/**
 * *Planned* in the task panel's Timing section (spec §9.6): the days and times the planner has this task on, each a
 * link to that day in the planner, with an ✕ that takes it off the plan. The planner is the only place they are
 * set; the task itself does not change.
 */
export function PlannedSlots({
  slots,
  ro,
  write,
}: {
  slots: PlannedSlot[];
  ro: boolean;
  write(fn: (doc: LoroDoc) => void): void;
}) {
  const { t, i18n } = useTranslation();
  if (!slots.length) return null;
  const day = (d: string) =>
    new Date(`${d}T12:00:00`).toLocaleDateString(i18n.language, {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  return (
    <div className="col-span-2 flex flex-col gap-1" data-testid="task-planned">
      <span className="text-[12.5px] text-neutral-500">{t("task.planned")}</span>
      <ul className="flex flex-col gap-1">
        {slots.map((s) => (
          <li key={s.day} className="flex items-center gap-2 text-[13px]" data-testid="task-planned-slot">
            <Link to={`/planner/${s.day}`} className="text-accent hover:underline">
              {day(s.day)}
            </Link>
            <span className="font-mono text-[12px] text-neutral-600 dark:text-neutral-300">
              {slotTime(s) ?? t("task.plannedAnyTime")}
            </span>
            {!ro && (
              <button
                type="button"
                aria-label={t("planner.unplan")}
                title={t("planner.unplan")}
                onClick={() => write((doc) => unplanTask(doc, s.day, s.taskId))}
                className="grid h-5 w-5 place-items-center rounded-sm text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-neutral-800"
                data-testid="task-planned-remove"
              >
                <X size={12} aria-hidden />
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
